import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Receipts, RECEIPT_CATEGORIES, PAYMENT_METHODS } from "@/lib/api";
import { ArrowLeft, Loader2, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import TagInput from "@/components/TagInput";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const Field = ({ label, children, testid }) => (
  <label data-testid={testid} className="block">
    <div className="swiss-label mb-1">{label}</div>
    {children}
  </label>
);

const ReceiptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    let cancel = false;
    Receipts.get(id)
      .then((r) => {
        if (cancel) return;
        setReceipt(r);
        setForm({
          vendor: r.vendor || "",
          receipt_date: r.receipt_date || "",
          total_amount: String(r.total_amount ?? ""),
          gst_amount: String(r.gst_amount ?? ""),
          payment_method: r.payment_method || "",
          category: r.category || "Other",
          notes: r.notes || "",
          tags: r.tags || [],
        });
      })
      .catch(() => toast.error("Receipt not found"))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [id]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const updated = await Receipts.update(id, {
        vendor: form.vendor,
        receipt_date: form.receipt_date,
        total_amount: Number(form.total_amount) || 0,
        gst_amount: Number(form.gst_amount) || 0,
        payment_method: form.payment_method,
        category: form.category,
        notes: form.notes,
      });
      setReceipt(updated);
      toast.success("Receipt updated.");
    } catch (e) {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this receipt? This cannot be undone.")) return;
    try {
      await Receipts.remove(id);
      toast.success("Receipt deleted.");
      navigate("/receipts");
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  if (loading) {
    return <div className="text-sm text-neutral-500">Loading…</div>;
  }
  if (!receipt) {
    return (
      <div className="text-sm text-neutral-500">Receipt not found.</div>
    );
  }

  return (
    <div data-testid="receipt-detail" className="fade-rise">
      <button
        onClick={() => navigate("/receipts")}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back to receipts
      </button>

      <div className="swiss-label mb-3">Receipt</div>
      <h1 className="font-display font-black text-4xl tracking-tight leading-tight">
        {receipt.vendor}
      </h1>
      <div className="font-display font-black text-3xl mono mt-2">
        {fmt(receipt.total_amount)}
      </div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mt-2">
        {receipt.category} · {receipt.receipt_date}
        {receipt.payment_method ? ` · ${receipt.payment_method}` : ""}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        {/* Image side */}
        <div>
          {receipt.image_base64 ? (
            <img
              data-testid="receipt-image"
              src={`data:${receipt.image_mime || "image/jpeg"};base64,${receipt.image_base64}`}
              alt={receipt.vendor}
              className="w-full border-2 border-black"
            />
          ) : (
            <div className="border-2 border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
              No photo on file.
            </div>
          )}
        </div>

        {/* Edit side */}
        <div className="space-y-5">
          <Field label="Vendor / Shop" testid="field-vendor">
            <Input
              data-testid="input-vendor"
              className="swiss-input"
              value={form.vendor}
              onChange={update("vendor")}
            />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Date" testid="field-date">
              <Input
                data-testid="input-date"
                type="date"
                className="swiss-input"
                value={form.receipt_date}
                onChange={update("receipt_date")}
              />
            </Field>
            <Field label="Total" testid="field-total">
              <Input
                data-testid="input-total"
                className="swiss-input mono"
                inputMode="decimal"
                value={form.total_amount}
                onChange={update("total_amount")}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Field label="GST" testid="field-gst">
              <Input
                data-testid="input-gst"
                className="swiss-input mono"
                inputMode="decimal"
                value={form.gst_amount}
                onChange={update("gst_amount")}
              />
            </Field>
            <Field label="Payment Method" testid="field-payment-method">
              <select
                data-testid="input-payment-method"
                className="swiss-input w-full appearance-none cursor-pointer"
                value={form.payment_method}
                onChange={update("payment_method")}
              >
                <option value="">— Select —</option>
                {PAYMENT_METHODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Category" testid="field-category">
            <div className="flex flex-wrap gap-2">
              {RECEIPT_CATEGORIES.map((c) => {
                const active = form.category === c;
                return (
                  <button
                    type="button"
                    key={c}
                    data-testid={`category-${c}`}
                    onClick={() => setForm((f) => ({ ...f, category: c }))}
                    className={`px-3 py-2 border-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                      active
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Notes" testid="field-notes">
            <Textarea
              data-testid="input-notes"
              className="swiss-input resize-none"
              rows={3}
              value={form.notes}
              onChange={update("notes")}
            />
          </Field>
          <Field label="Job tags" testid="field-tags">
            <TagInput
              testid="receipt-tags"
              value={form.tags}
              onChange={(tags) => setForm({ ...form, tags })}
              placeholder="Add job tag (e.g. smith-12-hill)"
            />
          </Field>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-200">
            <button
              data-testid="save-btn"
              onClick={save}
              disabled={saving}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save changes
            </button>
            <button
              data-testid="delete-btn"
              onClick={remove}
              className="border-2 border-red-600 text-red-600 font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-red-600 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptDetail;
